import cn from 'classnames';
import { Core, Form, Localization, toUtcIso, Typography } from 'connex-cds';
import { DateTime } from 'luxon';
import React from 'react';
import SignaturePad from 'react-signature-pad-wrapper';
import styled from 'styled-components';
import style from './style';
import { find } from 'lodash';
import { useTicketContext } from '../../TicketContext';

const { Translate } = Localization;

const Styled = styled.div`
  ${style}
`;

export const DamageWaiverSignatureCanvas = ({ damageWaiverSignature, onChange, ticketId }) => {
  const signaturePadRef = React.useRef();
  const { formatLongDateTime } = Localization.useFormat();
  const { ticket } = useTicketContext();
  const {
    Components: { DamageWaiverSignatureSiteContact },
    setFieldValue,
    values,
  } = Form.useFormContext();

  React.useEffect(() => {
    if (signaturePadRef.current) {
      const canvas = signaturePadRef.current.signaturePad.canvas;
      canvas.width = 930;
      canvas.height = 225;
    }
  }, []);

  const handleStrokeEnd = React.useCallback(() => {
    const now = toUtcIso(DateTime.now());

    onChange({
      ...(values?.damageWaiverSignature || {}),
      timestamp: now,
      formattedTimestamp: formatLongDateTime(now),
      image: signaturePadRef?.current?.signaturePad?.toDataURL?.(),
      ticketId,
    });
  }, [formatLongDateTime, onChange, ticketId, values?.damageWaiverSignature]);

  React.useEffect(() => {
    if (damageWaiverSignature?.image) {
      signaturePadRef.current.fromDataURL(damageWaiverSignature.image);
    }
  }, [damageWaiverSignature, signaturePadRef]);

  React.useEffect(() => {
    const ref = signaturePadRef?.current;
    signaturePadRef?.current?.signaturePad?.addEventListener?.('endStroke', handleStrokeEnd);
    return () => ref?.removeEventListener?.('endStroke', handleStrokeEnd);
  }, [handleStrokeEnd, signaturePadRef]);

  React.useEffect(() => {
    const contact = find(ticket?.destination?.contacts, { contactRole: 'general' });
    if (!values?.damageWaiverSignature?.signerName && contact?.name) {
      setFieldValue('damageWaiverSignature', { ...values?.damageWaiverSignature, signerName: contact.name });
    }
  }, [setFieldValue, ticket?.destination?.contacts]);

  const handleClear = React.useCallback(() => {
    signaturePadRef?.current?.clear?.();
    onChange({
      ...(values?.damageWaiverSignature || {}),
      timestamp: undefined,
      formattedTimestamp: undefined,
      image: undefined,
    });
  }, [values?.damageWaiverSignature]);

  return (
    <Styled className={cn('signature')}>
      <DamageWaiverSignatureSiteContact />
      <div className="instruction">
        <Typography.Subtitle>
          <Translate stringId="signatureInstruction" />
        </Typography.Subtitle>
      </div>
      <div className="signature-outer x">
        <Core.Button onClick={handleClear} stringId="clear-signature-button" data-testid="clear-signature-button" />
        <div className="signature-inner x">
          <SignaturePad redrawOnResize={true} ref={signaturePadRef} width={930} height={225} />
        </div>
      </div>
    </Styled>
  );
};
